import { useEffect, useRef, useState } from 'react';

import type { SchemaLoanRequestResolver_GetProcessedNotifications } from 'cpool-tables/backend-resolvers';

import { NOTIFICATION_CATEGORY_ORDER } from './notificationCategories';
import { ProcessedGroup } from './ProcessedGroup';

const PAGE_SIZE = 5;

type Props = {
    entries: SchemaLoanRequestResolver_GetProcessedNotifications;
    onNavigate: () => void;
};

export const ProcessedList: React.FC<Props> = ({ entries, onNavigate }) => {
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || visibleCount >= entries.length) return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, entries.length));
                }
            },
            { threshold: 0.1 },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [entries.length, visibleCount]);

    // Entries arrive sorted newest-first; we page over that order but render them grouped so each
    // category heading appears only once, gathering all currently-visible rows of that kind.
    const visible = entries.slice(0, visibleCount);

    return (
        <div className="flex flex-col divide-y divide-grey-stroke max-h-80 overflow-y-auto overflow-x-hidden">
            {NOTIFICATION_CATEGORY_ORDER.map((kind) => {
                const groupEntries = visible.filter((entry) => entry.kind === kind);
                if (groupEntries.length === 0) return null;
                return <ProcessedGroup key={kind} kind={kind} entries={groupEntries} onNavigate={onNavigate} />;
            })}
            {visibleCount < entries.length && <div ref={sentinelRef} className="h-4 shrink-0" aria-hidden />}
        </div>
    );
};
